// src/components/AddAccountMaster.jsx

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Spinner,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import Select from "react-select";

function AddAccountMaster() {
  const location = useLocation();
  const navigate = useNavigate();

  const [editId, setEditId] = useState(null); // account_master_id if editing
  const [submitting, setSubmitting] = useState(false);

  // dropdown data
  const [plbsGroups, setPlbsGroups] = useState([]);
  const [scheduleCodes, setScheduleCodes] = useState([]);
  const [accountNatures, setAccountNatures] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [roles, setRoles] = useState([]); // reference accounts (Role Type)

  // user/session
  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  // form state
  const [form, setForm] = useState({
    group_master_id: "", // plbs_group_master_id
    schedule_code_id: "", // sch_group_master_id
    account_code: "", // acc_code
    is_active: "Active", // UI dropdown → bool
    account_name: "", // name_on_account
    account_nature_id: "", // accountnature_id
    opening_balance: "",
    account_type_id: "", // accounttype_id
    voucher_prefix: "",

    // Role Type multi-select (list of account_master ids)
    account_master_ids: [],

    // bank and other details (for AccountNature == 2)
    bankaccountno: "",
    project_id: "",
    ifs_code: "",
    branch_name: "",
    micr_code: "",
    pan_no: "",
    gst_no: "",

    school_id: userObj.school_id || "",
  });

  // ---------- Fetchers ----------

  const fetchPlbsGroups = async (_level = 0) => {
    try {
      const response = await axios.get(`${baseUrl}/plbsgroups`, {
        params: { _level },
      });
      setPlbsGroups(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching P&L/BS groups:", error);
      setPlbsGroups([]);
      toast.error("Failed to load P&L/BS groups");
    }
  };

  // 🔹 Accept group_master_id as param, don't depend on form inside
  const fetchScheduleCodes = async (_group_master_id) => {
    if (!_group_master_id) {
      setScheduleCodes([]);
      return;
    }
    try {
      const res = await axios.get(`${baseUrl}/schedulecodes/`, {
        params: { _group_master_id },
      });
      setScheduleCodes(res.data || []);
    } catch (e) {
      console.error("Error fetching schedule codes:", e);
      setScheduleCodes([]);
      toast.error("Failed to load schedule codes");
    }
  };

  const fetchAccountNatures = async () => {
    try {
      const res = await axios.get(`${baseUrl}/accountnatures`);
      setAccountNatures(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Error fetching account natures:", e);
      setAccountNatures([]);
      toast.error("Failed to load account natures");
    }
  };

  const fetchAccountTypes = async () => {
    try {
      const res = await axios.get(`${baseUrl}/accounttypes`);
      setAccountTypes(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Error fetching account types:", e);
      setAccountTypes([]);
      toast.error("Failed to load account types");
    }
  };

  const fetchRoles = async (_account_master_id = 0) => {
    try {
      const res = await axios.get(
        `${baseUrl}/GetGroupAccountswithoutaccount_master_id`,
        { params: { _account_master_id } }
      );
      setRoles(res.data || []); // list of accounts for Role Type
    } catch (e) {
      console.error("Error fetching roles:", e);
      setRoles([]);
      toast.error("Failed to load accounts");
    }
  };

  // ---------- Options & derived flags ----------

  const scheduleCodeOptions = useMemo(
    () =>
      (scheduleCodes || []).map((s) => ({
        value: s.group_master_id,
        label: `${s.group_code} - ${s.child_description || s.description || ""}`,
      })),
    [scheduleCodes]
  );

  const roleOptions = useMemo(
    () =>
      (roles || []).map((r) => ({
        value: r.account_master_id,
        label: r.name_on_account,
      })),
    [roles]
  );

  const activeOptions = useMemo(
    () => [
      { value: "Active", label: "Active" },
      { value: "Inactive", label: "Inactive" },
    ],
    []
  );

  // Bank nature (2) => show voucher/bank section
  // const isBankNature = useMemo(
  //   () => String(form.account_nature_id) === "2",
  //   [form.account_nature_id]
  // );

  // If AccountNature == 3 => enable Role Type
  const isRoleTypeEnabled = useMemo(
    () => String(form.account_nature_id) === "3",
    [form.account_nature_id]
  );

  // Clear Role Type selections if not allowed
  useEffect(() => {
    if (!isRoleTypeEnabled && (form.account_master_ids || []).length > 0) {
      setForm((prev) => ({ ...prev, account_master_ids: [] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRoleTypeEnabled]);

  // Clear bank fields when not Bank nature
 
const normalizeIsActive = (val) => {
  
  if (typeof val === "string") {
    const v = val.trim().toLowerCase();

    if (["active"].includes(v)) {
      return "Active";
    }

    // handle "In-active", "inactive", etc.
    if (["in-active", "inactive"].includes(v)) {
      return "Inactive";
    }
  }

  // default
  return "Active";
};

  // ---------- Initial load + edit mode ----------

  useEffect(() => {
    const accountData = location.state?.accountData; // optional
   

    fetchPlbsGroups(0);
    fetchAccountNatures();
    fetchAccountTypes();

    // 🔹 Call roles API AFTER navigation using the current account_master_id
    const currentAccountMasterId = accountData?.account_master_id || 0;
    fetchRoles(currentAccountMasterId);

   if (accountData) {
  setEditId(accountData.account_master_id);

  // 👇 take from activestatus if present, else is_active
  const rawIsActive = 
    accountData.activestatus !== undefined && accountData.activestatus !== null
      ? accountData.activestatus
      : accountData.is_active;

  const isActive = normalizeIsActive(rawIsActive);

      let accountMasterIds = [];
      const str = accountData.strplbs_group_codeid
        ? String(accountData.strplbs_group_codeid)
        : accountData.reference_account_master_id
          ? String(accountData.reference_account_master_id)
          : "";

      if (str && str.trim() !== "") {
        accountMasterIds = str
          .split(",")
          .map((v) => v.trim())
          .filter((v) => v !== "");
      }

      setForm((prev) => ({
        ...prev,
        group_master_id: accountData.plbs_group_master_id
          ? String(accountData.plbs_group_master_id)
          : "",
        schedule_code_id: accountData.sch_group_master_id
          ? String(accountData.sch_group_master_id)
          : "",
        account_code: accountData.acc_code || "",
        account_name: accountData.name_on_account || "",
        account_nature_id: accountData.accountnature_id
          ? String(accountData.accountnature_id)
          : "",
        account_type_id: accountData.accounttype_id
          ? String(accountData.accounttype_id)
          : "",
        account_master_ids: accountMasterIds,
        opening_balance:
          accountData.opening_balance !== null &&
            accountData.opening_balance !== undefined
            ? String(accountData.opening_balance)
            : "",
        voucher_prefix: accountData.voucher_prefix || "",
        bankaccountno: accountData.bankaccountno || "",
        project_id: accountData.project_id ? String(accountData.project_id) : "",
        ifs_code: accountData.ifs_code || "",
        branch_name: accountData.branch_name || "",
        micr_code: accountData.micr_code || "",
        pan_no: accountData.pan_no || "",
        gst_no: accountData.gst_no || "",
        is_active: isActive,
        school_id: userObj.school_id || "",
      }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 🔹 Whenever group_master_id changes (including on Edit), call /schedulecodes
  useEffect(() => {
    if (form.group_master_id) {
      fetchScheduleCodes(form.group_master_id);
    } else {
      setScheduleCodes([]);
    }
  }, [form.group_master_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAccountCodeDuplicate = async (code) => {
    const trimmed = (code || "").trim();
    if (!trimmed) return;

    try {
      const res = await axios.get(`${baseUrl}/duplicationchecaccode`, {
        params: {
          _name: trimmed,
          _flag: "ACCM",
          _code_type: null,
          _id: editId || 0,
        },
      });

      const exists = Number(
        Array.isArray(res.data)
          ? res.data[0]?.check_duplication_gstmaster
          : res.data?.check_duplication_gstmaster
      );

      if (exists === 1) {
        toast.warn("Record Already Exists With Same Account Code");

        // 🔴 CLEAR ONLY THE ACCOUNT CODE FIELD
        setForm((prev) => ({
          ...prev,
          account_code: "",
        }));
      }
    } catch (err) {
      console.error("Error checking duplicate account code:", err);
    }
  };

  // ---------- Handlers ----------

  const resetForm = () => {
    setForm({
      group_master_id: "",
      schedule_code_id: "",
      account_code: "",
      is_active: "Active",
      account_name: "",
      account_nature_id: "",
      opening_balance: "",
      account_type_id: "",
      voucher_prefix: "",
      account_master_ids: [],
      bankaccountno: "",
      project_id: "",
      ifs_code: "",
      branch_name: "",
      micr_code: "",
      pan_no: "",
      gst_no: "",
      school_id: userObj.school_id || "",
    });
    setEditId(null);
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;

    if (id === "opening_balance") {
      const cleaned = value.replace(/[^0-9.]/g, "");
      const parts = cleaned.split(".");
      const normalized =
        parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : cleaned;
      setForm((prev) => ({ ...prev, [id]: normalized }));
      return;
    }

    if (id === "group_master_id") {
      setForm((prev) => ({
        ...prev,
        group_master_id: value,
        schedule_code_id: "",
      }));
      return;
    }

    if (id === "account_code") {
      setForm((prev) => ({
        ...prev,
        account_code: value,
      }));
      // 🔁 Call duplication check whenever account code changes
      checkAccountCodeDuplicate(value);
      return;
    }

    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const validateForm = () => {
    if (!form.account_code?.trim()) return "Please enter Account Code";
    if (!form.account_name?.trim()) return "Please enter Account Name";
    if (!form.account_type_id) return "Please select Account Type";
    if (!form.account_nature_id) return "Please select Account Nature";
    if (!form.is_active) return "Please select Active status";
    if (!form.schedule_code_id) return "Please select Schedule Code";
    if (!form.group_master_id) return "Please select P&L/BS Group code";

   
    const ob = Number(form.opening_balance || 0);
    if (Number.isNaN(ob)) return "Opening Balance must be a number";

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const error = validateForm();
    if (error) {
      toast.warn(error);
      return;
    }

    const isActiveBool = form.is_active === "Active";
    const selectedRoleIds =
      Array.isArray(form.account_master_ids) &&
        form.account_master_ids.length > 0
        ? form.account_master_ids.map((id) => Number(id))
        : [];

    const payload = {
      account_master_id: editId || 0,
      acc_code: form.account_code,
      name_on_account: form.account_name,
      accountnature_id: Number(form.account_nature_id),
      voucher_prefix: form.voucher_prefix || null,
      is_active: isActiveBool,
      accounttype_id: Number(form.account_type_id),
      opening_balance: Number(form.opening_balance || 0),

      plbs_group_codeid: null,

      // reference_account_master_id is NOT sent here
      plbs_group_master_id: Number(form.group_master_id),
      sch_group_master_id: Number(form.schedule_code_id),

      created_by: userObj?.email || userObj?.user_name || "system",

      bankaccountno: form.bankaccountno || null,
      project_id: userObj.school_id,
      ifs_code: form.ifs_code || null,
      branch_name: form.branch_name || null,
      micr_code: form.micr_code || null,
      pan_no: form.pan_no || null,
      gst_no: form.gst_no || null,
    };

    try {
      setSubmitting(true);

      const res = await axios.post(`${baseUrl}/accountmaster`, payload);
      const accountMasterId = res?.data?.account_master_id || editId;

      if (!accountMasterId) {
        throw new Error("account_master_id not returned from server");
      }

      // Now send selected Role Types as group account details
      if (selectedRoleIds.length > 0) {
        await Promise.all(
          selectedRoleIds.map((id, index) =>
            axios.post(`${baseUrl}/accountmaster/groupaccountdetails`, {
              group_accountdetail_id: 0,
              account_master_id: accountMasterId,
              plbs_group_codeid: id,
              created_by: userObj?.email || userObj?.user_name || "system",
              flag: index + 1,
            })
          )
        );
      }

      if (editId) {
        toast.success("Account updated successfully");
      } else {
        toast.success("Account created successfully");
        resetForm();
      }
    } catch (err) {
      console.error("Submit error", err);
      const msg =
        err?.response?.data?.detail || err?.message || "Failed to save";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ---------- JSX ----------

  return (
    <Container fluid>
      <div className="pageBody">
        <ToastContainer />
        <Container fluid>
          <Card>
            <Card.Body>
              <form onSubmit={handleSubmit}>
                <Row>
                  <Col xs={12}>
                    <h6 className="commonSectionTitle">
                      {editId ? "Edit Account" : "Add Account"}
                    </h6>
                  </Col>
                </Row>

                {/* Row 1 */}
                <Row>
                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group controlId="group_master_id">
                        <Form.Label>
                          Parent Group Name{" "}
                          <span className="requiredStar">*</span>
                        </Form.Label>
                        <Form.Select
                          required
                          value={form.group_master_id}
                          onChange={handleInputChange}
                        >
                          <option value="" disabled hidden>
  Select  Parent Group Name
</option>

                          {plbsGroups.map((g) => (
                            <option
                              key={g.group_master_id}
                              value={g.group_master_id}
                            >
                              {g.group_code}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group controlId="schedule_code_id">
                        <Form.Label>
                          SUb Group Name{" "}
                          <span className="requiredStar">*</span>
                        </Form.Label>
                        <Select
                          inputId="schedule_code_id"
                          classNamePrefix="react-select"
                          placeholder="Select Schedule Code"
                          options={scheduleCodeOptions}
                          isClearable
                          isDisabled={!form.group_master_id}
                          value={
                            scheduleCodeOptions.find(
                              (opt) =>
                                String(opt.value) ===
                                String(form.schedule_code_id)
                            ) || null
                          }
                          onChange={(selected) => {
                            setForm((prev) => ({
                              ...prev,
                              schedule_code_id: selected
                                ? String(selected.value)
                                : "",
                            }));
                          }}
                        />
                      </Form.Group>
                    </div>
                  </Col>
                  
                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group controlId="account_name">
                        <Form.Label>
                          Account Name{" "}
                          <span className="requiredStar">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={form.account_name}
                          onChange={handleInputChange}
                          placeholder="Enter Account Name"
                          required
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group controlId="account_code">
                        <Form.Label>
                          Account Code <span className="requiredStar">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          value={form.account_code}
                          onChange={handleInputChange}
                          placeholder="Enter Account Code"
                          required
                          disabled={!!editId}
                        />
                      </Form.Group>

                    </div>
                  </Col>

                </Row>

                {/* Row 2 */}
                <Row>
                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group controlId="account_nature_id">
                        <Form.Label>
                          Account Nature{" "}
                          <span className="requiredStar">*</span>
                        </Form.Label>
                        <Form.Select
                          required
                          value={form.account_nature_id}
                          onChange={handleInputChange}
                        >
                          <option value="" disabled hidden>Select Account Nature</option>
                          {accountNatures.map((n) => (
                            <option
                              key={n.accountnature_id}
                              value={n.accountnature_id}
                            >
                              {n.accountnature_description}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group controlId="account_master_ids">
                        <Form.Label>Role Type</Form.Label>
                        <Select
                          inputId="account_master_ids"
                          classNamePrefix="react-select"
                          isMulti
                          isDisabled={!isRoleTypeEnabled}
                          options={roleOptions}
                          value={roleOptions.filter((opt) =>
                            (form.account_master_ids || [])
                              .map(String)
                              .includes(String(opt.value))
                          )}
                          onChange={(selectedOptions) => {
                            setForm((prev) => ({
                              ...prev,
                              account_master_ids: (selectedOptions || []).map(
                                (opt) => String(opt.value)
                              ),
                            }));
                          }}
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group controlId="account_type_id">
                        <Form.Label>
                          Account Type{" "}
                          <span className="requiredStar">*</span>
                        </Form.Label>
                        <Form.Select
                          required
                          value={form.account_type_id}
                          onChange={handleInputChange}
                        >
                          <option value="" disabled hidden>Select Account Type</option>
                          {accountTypes.map((t) => (
                            <option
                              key={t.accounttype_id}
                              value={t.accounttype_id}
                            >
                              {t.accounttype_description}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group controlId="opening_balance">
                        <Form.Label>Opening Balance</Form.Label>
                        <Form.Control
                          type="text"
                          inputMode="decimal"
                          maxLength={150}
                          value={form.opening_balance}
                          onChange={handleInputChange}
                          placeholder="0.00"
                        />
                      </Form.Group>
                    </div>
                  </Col>
                </Row>

                {/* Row 3 */}
                <Row>
                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group controlId="is_active">
                        <Form.Label>
                          Is Active{" "}
                          <span className="requiredStar">*</span>
                        </Form.Label>
                        <Form.Select
                          required
                          value={form.is_active}
                          onChange={handleInputChange}
                        >
                          {activeOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </Col>
                </Row>

                <div className="d-flex justify-content-between mt-3">
                  <div>
                    <Button
                      type="button"
                      className="btn btn-info clearBtn"
                      onClick={resetForm}
                    
                    >
                      Reset
                    </Button>
                  </div>
                  <div>
                    <Button
                      type="button"
                      variant="danger"
                      className="secondaryBtn"
                      onClick={() => navigate(-1)}
                     
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      className="btn btn-success primaryBtn"
                     
                    >
                      
                        Submit
                      
                    </Button>
                  </div>
                </div>
              </form>
            </Card.Body>
          </Card>
        </Container>
      </div>
    </Container>
  );
}

export default AddAccountMaster;
